import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { PlusOutlined } from '@ant-design/icons'
import { Col, Form, Row, Typography, Upload } from 'antd'
import _ from 'lodash'

import { api, request } from '../../util/handlerRequestUtil'
import { getMessage } from '../../util/IntlMessages'
import { Preview } from './components'

import './styles.less'

/*
  The props for this component are not optimized.

  However, since it currently shares the received props with other custom inputs, modifying them would require double-checking every single custom input. Therefore, it has been decided to leave it as-is for now.

  Summary: If src/components/CustomFieldRender/index.js is updated in the future, please review this component to improve the props definition.
*/

export default function CustomUpload({
  field,
  name,
  value,
  errors,
  rowSpan,
  setFieldValue,
  fieldProperties,
  disabled = false,
  required = false,
  ...props
}) {
  const [fileList, setFileList] = useState([])
  const [preview, setPreview] = useState(null)

  useEffect(
    function setInitialFiles() {
      if (fileList.length === 0 && value[name] && value[name]?.length > 0) {
        const savedFiles = value[name]?.map(({ file }) => ({
          uid: file.id.toString(),
          name: file.caption,
          status: 'done',
          url: file.fileUri,
          response: { ...file }
        }))
        setFileList(savedFiles)
      }
    },
    [fileList.length, value, name]
  )

  const customRequest = useCallback(async (options) => {
    try {
      const data = new FormData()
      data.append('file', options.file)

      const upload_response = await request(api.uploadAttachment(data))

      options.onSuccess(upload_response, options.file)
    } catch (error) {
      options.onError(error)
    }
  }, [])

  const onChange = useCallback(
    (changedData) => {
      const { file: changedFile, fileList: changedFileList } = changedData

      if (changedFile.status === 'done') {
        const files = [...(value[name] || []), { file: changedFile.response }]
        setFieldValue(name, files)

        changedFile.url = changedFile.response?.fileUri || ''
        setFileList(changedFileList.map((f) => (f.uid === changedFile.uid ? changedFile : f)))

        return
      }

      setFileList(changedFileList)
    },
    [name, setFieldValue, value]
  )

  const removeFile = useCallback(
    (file) => {
      const filteredFiles = fileList.filter((item) => item.response.id !== file.response.id)
      setFileList(filteredFiles)

      const fieldValue = filteredFiles.map(({ response }) => ({ file: response }))
      setFieldValue(name, fieldValue)
    },
    [name, setFieldValue, fileList]
  )

  const setCaptionValue = useCallback(
    (fieldName, newValue) => {
      const files = _.cloneDeep(value[name] || [])
      const fileId = fieldName.split('_')[1]
      const targetFileIndex = files.findIndex((item) => item.file.id == fileId)

      files[targetFileIndex].file.caption = newValue

      setFieldValue(name, files)
      setPreview({ ...preview, caption: newValue })
    },
    [name, preview, value, setFieldValue]
  )

  const showMessageError = useCallback(() => {
    const msgError = errors[name]
    if (!msgError) return null

    return (
      <Typography.Text type="danger" className="customUploadComponent__error">
        {msgError}
      </Typography.Text>
    )
  }, [errors, name])

  const maxFileSize = useMemo(() => {
    const { fileSize, sizeType } = fieldProperties

    return `${fileSize} ${sizeType}`
  }, [fieldProperties])

  const acceptedFileTypes = useMemo(() => {
    return fieldProperties?.fileType?.join(',') || '*/*'
  }, [fieldProperties?.fileType])

  return (
    <>
      <Col xs={24} sm={24} md={rowSpan} lg={rowSpan} xl={rowSpan} id={props.id} className="customUploadComponent">
        <Form.Item
          label={
            <div className="customUploadComponent__label">
              {field}
              {required && <span className="customUploadComponent__requiredAsterisk">*</span>}
            </div>
          }
        >
          <Row>
            <Upload
              key={name}
              accept={acceptedFileTypes}
              customRequest={customRequest}
              disabled={disabled}
              fileList={fileList}
              listType="picture-card"
              showUploadList={{
                showDownloadIcon: true
              }}
              onChange={onChange}
              onPreview={({ response }) => setPreview(response)}
              onRemove={removeFile}
            >
              <div>
                <PlusOutlined />
                <div className="ant-upload-text">{getMessage('select.attachment', maxFileSize)}</div>
              </div>
            </Upload>
            {showMessageError()}
          </Row>
        </Form.Item>
      </Col>
      <Preview
        preview={preview}
        setPreview={setPreview}
        field={field}
        errors={errors}
        setCaptionValue={setCaptionValue}
      />
    </>
  )
}
